import { Card } from "@/src/components/wrappers/Card";
import { Content } from "@/src/components/wrappers/Content";
import { ParseError, root } from "./amenities-query-parser";
import { useDebounce } from "@/src/lib/useDebounce";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/src/lib/utils";
import { Button } from "@/src/components/ui/button";
import { useFetch } from "@/src/lib/useFetch";
import { AmenityGroup, AmenityShort } from "./model";
import { TooltipOnEllipsis } from "@/src/components/ui/tooltip-on-ellipsis";
import { FormItem } from "@/src/components/ui/form";
import { Input } from "@/src/components/ui/input";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "@/src/components/ui/use-toast";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/src/components/ui/tooltip";
import { InfoIcon } from "lucide-react";
import { Checkbox } from "@/src/components/ui/checkbox";

const parser = root();

export function EditConfig() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [isWaterToy, setIsWaterToy] = useState(false);

  const [amenities, setAmenities] = useState([] as AmenityShort[]);

  const { fetch, isFetching } = useFetch();

  useEffect(() => {
    if (id !== "add") {
      fetch<{ data: AmenityGroup }>(`/api/amenities/group/${id}`).then((r) => {
        ref.current && (ref.current.firstChild!.textContent = r.data.config ?? null);
        setName(r.data.name);
        setIsWaterToy(r.data.isWaterToy);
      });
    }
  }, []);

  const onEdit = useDebounce((text: string | null) => {
    try {
      parser(text || "", 0);
      setError(null);
    } catch (e) {
      const err = e as ParseError;
      setError(err.message);
    }
  }, 500);

  const onSave = () => {
    const url = id !== "add" ? `/api/amenities/group/${id}` : `/api/amenities/groups`;
    const method = id !== "add" ? "PUT" : ("POST" as const);
    const text = ref.current?.textContent;
    fetch<{ data: { id: string } }>(url, method, {
      name,
      isWaterToy: isWaterToy,
      query: text,
    })
      .then((res) => {
        toast({
          title: "Saved amenity group",
        });
        navigate(`/amenities/${res.data.id}`);
      })
      .catch(() => {
        toast({
          title: "Error",
          description: "Failed to save amenity group",
          variant: "destructive",
        });
      });
  };

  const ref = useRef<HTMLDivElement>(null);

  const loadMatchingAmenities = async () => {
    const text = ref.current?.textContent;
    const res = await fetch<{ data: AmenityShort[] }>(`/api/amenities/query`, "POST", {
      query: text,
    });
    setAmenities(res.data);
  };

  const deleteGroup = async () => {
    try {
      await fetch(`/api/amenities/group/${id}`, "DELETE");
      toast({
        title: "Amenity group deleted",
      });
      navigate("/amenities");
    } catch (e: unknown) {
      toast({
        title: "Error",
        description: "An error has occurred while deleting amenity group",
        variant: "destructive",
      });
    }
  };

  return (
    <Content
      title={id === "add" ? "Create amenity group" : `Edit group: ${name}`}
      contentAction={
        id !== "add" && (
          <Button onClick={deleteGroup} variant="destructive" disabled={name == "Skipper" || isFetching}>
            Delete group
          </Button>
        )
      }
    >
      <Card className="flex flex-col gap-1">
        <div className="grid grid-cols-3 gap-4 mb-1 items-end">
          <FormItem>
            <label className="text-md font-semibold">Name</label>
            <Input value={name} onChange={(e) => setName(e.currentTarget.value)} />
          </FormItem>
          <FormItem className="flex items-center gap-2">
            <label className="text-md font-semibold">Water Toy</label>
            <Checkbox checked={isWaterToy} onCheckedChange={(e) => setIsWaterToy(e === true)} />
          </FormItem>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <label className="text-md font-semibold flex gap-2 items-center w-fit">
              Config
              <InfoIcon size={16} />
            </label>
          </TooltipTrigger>
          <TooltipContent align="start">
            Use string literals as search values - ex. <code>"query"</code>
            <br />
            Use <code>and</code> and <code>or</code> to combine queries - ex. <code>"skipper" or "hostess"</code>
            <br />
            Use <code>not</code> to negate query - ex. <code>not "training"</code>
            <br />
            Use <code>near</code> to get fuzzy match - ex. <code>near "barbecue"</code>
            <br />
            Use <code>paid</code> to get paid amenities
          </TooltipContent>
        </Tooltip>
        <div
          ref={ref}
          onInput={(ev) => {
            onEdit(ev.currentTarget.textContent);
          }}
          className={cn(
            "p-2 flex items-stretch w-full min-h-24 max-w-[1000px] resize-y border overflow-hidden rounded-md",
            error && "border-destructive",
          )}
        >
          <code contentEditable className="w-full block outline-none"></code>
        </div>
        {error && <div className="text-destructive">{error}</div>}

        <div className="flex mt-2 gap-2">
          <Button disabled={isFetching || !!error} onClick={loadMatchingAmenities}>
            Test
          </Button>
          <Button disabled={isFetching || !!error} onClick={onSave}>
            Save
          </Button>
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold">Amenities ({amenities.length} total)</h3>
        <div className="flex flex-col gap-1">
          {amenities.map((amenity) => (
            <div key={amenity.text} className="flex gap-8 justify-between">
              <TooltipOnEllipsis className="overflow-x-hidden text-ellipsis text-nowrap whitespace-nowrap">
                {amenity.text}
              </TooltipOnEllipsis>
              <div className="min-w-36">Yacht count: {amenity.yachtCount}</div>
            </div>
          ))}
        </div>
      </Card>
    </Content>
  );
}
